import type { BankedProfile } from "../simulation/persistence";
import { BUILD_SHORT_SHA } from "../build-info";
import { readShowFpsPreference, writeShowFpsPreference } from "./fps-overlay";

export type SettingsOverlayHandlers = {
  onSettings: (settings: BankedProfile["settings"]) => void;
  onShowFps?: (show: boolean) => void;
};

/**
 * Dedicated systems screen. Opens from the hangar, pause, or the always-on gear.
 * Not mixed into the carrier bay.
 */
export class SettingsOverlay {
  private readonly root: HTMLElement;
  private readonly screen: HTMLElement;
  private readonly fab: HTMLButtonElement;
  private profile: BankedProfile | null = null;
  private openState = false;

  public constructor(
    host: HTMLElement,
    private readonly handlers: SettingsOverlayHandlers,
  ) {
    this.root = document.createElement("div");
    this.root.className = "settings-root";
    this.root.innerHTML = `
      <button type="button" class="settings-fab" data-settings-open aria-label="Open settings">SYS</button>
      <div class="settings-screen" data-settings-screen hidden>
        <div class="settings-panel" role="dialog" aria-labelledby="settings-title">
          <header>
            <div>
              <span class="settings-kicker">CARRIER SYSTEMS</span>
              <strong id="settings-title">Settings</strong>
            </div>
            <button type="button" class="settings-close" data-settings-close>Close</button>
          </header>
          <div class="settings-grid">
            <section>
              <h3>Combat read</h3>
              <div class="settings-toggles">
                <button type="button" class="settings-toggle" data-toggle="focus" aria-pressed="true">Focus falloff</button>
                <button type="button" class="settings-toggle" data-toggle="colorblind" aria-pressed="false">Colorblind telegraphs</button>
                <button type="button" class="settings-toggle" data-toggle="motion" aria-pressed="false">Reduced motion</button>
                <button type="button" class="settings-toggle" data-toggle="touch" aria-pressed="false">Force touch sticks</button>
              </div>
            </section>
            <section>
              <h3>Audio</h3>
              <div class="settings-toggles">
                <button type="button" class="settings-toggle" data-toggle="music" aria-pressed="true">Music</button>
                <button type="button" class="settings-toggle" data-toggle="mute" aria-pressed="false">Mute all</button>
              </div>
              <div class="volume-sliders">
                <label>
                  <span>SFX</span>
                  <input type="range" data-sfx-vol min="0" max="100" value="100" />
                  <output data-sfx-vol-out>100%</output>
                </label>
                <label>
                  <span>Music</span>
                  <input type="range" data-music-vol min="0" max="100" value="40" />
                  <output data-music-vol-out>40%</output>
                </label>
              </div>
            </section>
            <section>
              <h3>Picture</h3>
              <label class="settings-quality">Graphics
                <select data-quality>
                  <option value="auto">Auto</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </label>
              <div class="settings-toggles">
                <button type="button" class="settings-toggle" data-toggle="fps" aria-pressed="false">Show FPS</button>
              </div>
            </section>
          </div>
          <footer class="build-provenance">BUILD <code>${BUILD_SHORT_SHA}</code></footer>
        </div>
      </div>
    `;
    host.appendChild(this.root);
    this.screen = this.root.querySelector("[data-settings-screen]")!;
    this.fab = this.root.querySelector("[data-settings-open]")!;
    this.bind();
  }

  public isOpen(): boolean {
    return this.openState;
  }

  public open(): void {
    this.openState = true;
    this.screen.hidden = false;
    this.fab.hidden = true;
    this.root.dataset.open = "true";
  }

  public close(): void {
    this.openState = false;
    this.screen.hidden = true;
    this.fab.hidden = false;
    delete this.root.dataset.open;
  }

  public sync(profile: BankedProfile): void {
    this.profile = profile;
    this.setToggle("focus", profile.settings.focusFalloff);
    this.setToggle("motion", profile.settings.reducedMotion);
    this.setToggle("touch", profile.settings.showTouchControls);
    this.setToggle("colorblind", profile.settings.colorblindTelegraphs);
    this.setToggle("music", profile.settings.musicEnabled);
    this.setToggle("mute", profile.settings.muted);
    this.setToggle("fps", readShowFpsPreference());
    const quality = this.root.querySelector<HTMLSelectElement>("[data-quality]");
    if (quality) quality.value = profile.settings.renderQuality;
    const sfxVol = this.root.querySelector<HTMLInputElement>("[data-sfx-vol]");
    const musicVol = this.root.querySelector<HTMLInputElement>("[data-music-vol]");
    const sfxOut = this.root.querySelector("[data-sfx-vol-out]");
    const musicOut = this.root.querySelector("[data-music-vol-out]");
    if (sfxVol) sfxVol.value = String(Math.round(profile.settings.sfxVolume * 100));
    if (musicVol) musicVol.value = String(Math.round(profile.settings.musicVolume * 100));
    if (sfxOut) sfxOut.textContent = `${Math.round(profile.settings.sfxVolume * 100)}%`;
    if (musicOut) musicOut.textContent = `${Math.round(profile.settings.musicVolume * 100)}%`;
  }

  public dispose(): void {
    window.removeEventListener("keydown", this.onKey);
    this.root.remove();
  }

  private setToggle(name: string, on: boolean): void {
    const btn = this.root.querySelector<HTMLButtonElement>(`[data-toggle="${name}"]`);
    if (!btn) return;
    btn.dataset.on = on ? "true" : "false";
    btn.setAttribute("aria-pressed", on ? "true" : "false");
  }

  private emit(): void {
    if (!this.profile) return;
    const pressed = (name: string, fallback: boolean): boolean => {
      const btn = this.root.querySelector<HTMLButtonElement>(`[data-toggle="${name}"]`);
      if (!btn) return fallback;
      return btn.dataset.on === "true";
    };
    const renderQuality =
      this.root.querySelector<HTMLSelectElement>("[data-quality]")?.value ??
      this.profile.settings.renderQuality;
    const sfxRaw = Number(
      this.root.querySelector<HTMLInputElement>("[data-sfx-vol]")?.value ?? 100,
    );
    const musicRaw = Number(
      this.root.querySelector<HTMLInputElement>("[data-music-vol]")?.value ?? 40,
    );
    const sfxVolume = Math.max(0, Math.min(1, sfxRaw / 100));
    const musicVolume = Math.max(0, Math.min(1, musicRaw / 100));
    const sfxOut = this.root.querySelector("[data-sfx-vol-out]");
    const musicOut = this.root.querySelector("[data-music-vol-out]");
    if (sfxOut) sfxOut.textContent = `${Math.round(sfxVolume * 100)}%`;
    if (musicOut) musicOut.textContent = `${Math.round(musicVolume * 100)}%`;
    const fpsOn = pressed("fps", false);
    writeShowFpsPreference(fpsOn);
    this.handlers.onShowFps?.(fpsOn);
    this.handlers.onSettings({
      focusFalloff: pressed("focus", true),
      reducedMotion: pressed("motion", false),
      showTouchControls: pressed("touch", false),
      colorblindTelegraphs: pressed("colorblind", false),
      musicEnabled: pressed("music", true),
      muted: pressed("mute", false),
      sfxVolume,
      musicVolume,
      renderQuality:
        renderQuality === "high" || renderQuality === "medium" || renderQuality === "low"
          ? renderQuality
          : "auto",
    });
  }

  private bind(): void {
    this.fab.addEventListener("click", () => this.open());
    this.root.querySelector("[data-settings-close]")?.addEventListener("click", () => this.close());
    this.screen.addEventListener("click", (event) => {
      if (event.target === this.screen) this.close();
    });
    this.root.addEventListener("click", (event) => {
      const toggle = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>(
        "[data-toggle]",
      );
      if (!toggle) return;
      const next = toggle.dataset.on !== "true";
      toggle.dataset.on = next ? "true" : "false";
      toggle.setAttribute("aria-pressed", next ? "true" : "false");
      this.emit();
    });
    this.root.querySelector("[data-quality]")?.addEventListener("change", () => this.emit());
    this.root.querySelector("[data-sfx-vol]")?.addEventListener("input", () => this.emit());
    this.root.querySelector("[data-music-vol]")?.addEventListener("input", () => this.emit());
    window.addEventListener("keydown", this.onKey);
  }

  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.code === "Escape" && this.openState) {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.code === "Comma" && !event.repeat && !this.openState) {
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      event.preventDefault();
      this.open();
    }
  };
}
